import React, { useState, useEffect, useReducer } from "react";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import moment from "moment";

import { readDocument, updateDocument } from "@/lib/db";
import { useAuth } from "@/lib/auth";
import SignUpSignIn from "@/components/user/SignUpSignIn";
import Button from "@/ui/Button";

import HomeHeader from "@/components/home/HomeHeader";

import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import { imageLoader } from "@/utils/image";

const defaultLivingPreferences = [
    {
        index: 1,
        section: "What community features matter most to you?",
        questions: [
            { question: "New construction", answer: 0 },
            { question: "Large Community (up too 100 units)", answer: 0 },
            { question: "Small Community (under 20 units)", answer: 0 },
            { question: "Elevator building", answer: 0 },
            { question: "Outdoor Parking", answer: 0 },
            { question: "Covered Parking", answer: 0 },
            { question: "Electric Car Chargers", answer: 0 },
        ],
    },
    {
        index: 2,
        section: "What community staffing matter most to you?",
        questions: [
            { question: "Property Manager Onsite", answer: 0 },
            { question: "Property Maintenance Onsite", answer: 0 },
            { question: "On premise nurse", answer: 0 },
            { question: "On premise physical therapist", answer: 0 },
            { question: "24-hour caregiving", answer: 0 },
        ],
    },
    {
        index: 3,
        section: "What community rules matter most to you?",
        questions: [
            { question: "Non smoking", answer: 0 },
            { question: "Smoking allowed", answer: 0 },
            { question: "Cats allowed", answer: 0 },
            { question: "Dogs allowed", answer: 0 },
            { question: "24 hour guest access", answer: 0 },
        ],
    },
    {
        index: 4,
        section: "What community care provisions matter most to you?",
        questions: [
            { question: "Oxygen", answer: 0 },
            { question: "Diabetic program", answer: 0 },
            { question: "Injections oversight", answer: 0 },
            { question: "Medication administration", answer: 0 },
        ],
    },
    {
        index: 5,
        section: "What community dining preferences matter most to you?",
        questions: [
            { question: "All meals provided", answer: 0 },
            { question: "In-room dining", answer: 0 },
            { question: "Communal dining", answer: 0 },
            { question: "Made-to-order meals", answer: 0 },
        ],
    },
    {
        index: 6,
        section: "What community food preparation matter most to you?",
        questions: [
            { question: "Vegetarian", answer: 0 },
            { question: "Organic", answer: 0 },
            { question: "Vegan", answer: 0 },
            { question: "Kosher", answer: 0 },
            { question: "Halal", answer: 0 },
            { question: "Gluten-Free", answer: 0 },
            { question: "Other foods allergies", answer: 0 },
        ],
    },
    {
        index: 7,
        section: "What amenities matter most to you?",
        questions: [
            { question: "Planned activities", answer: 0 },
            { question: "Wellness program", answer: 0 },
            { question: "Gym", answer: 0 },
            { question: "Theater", answer: 0 },
            { question: "Arts program", answer: 0 },
            { question: "Salon", answer: 0 },
            { question: "Pool", answer: 0 },
        ],
    },
    {
        index: 8,
        section: "What types of units are you considering?",
        questions: [
            { question: "Unfurnished", answer: 0 },
            { question: "Furnished", answer: 0 },
            { question: "Studio", answer: 0 },
            { question: "One-bedroom", answer: 0 },
            { question: "Two-bedroom", answer: 0 },
            { question: "Three-bedroom", answer: 0 },
            { question: "Shared room", answer: 0 },
        ],
    },
    {
        index: 9,
        section: "What unit features matter most to you?",
        questions: [
            { question: "Full kitchen", answer: 0 },
            { question: "Kitchenette", answer: 0 },
            { question: "Full, private bath", answer: 0 },
            { question: "Private entry", answer: 0 },
            { question: "Private outdoor space", answer: 0 },
            { question: "Washer/Dryer", answer: 0 },
            { question: "Dishwasher", answer: 0 },
        ],
    },
    {
        index: 10,
        section: "What unit location features matter most to you?",
        questions: [
            { question: "Close to elevator", answer: 0 },
            { question: "Close to caregivers", answer: 0 },
            { question: "Close to dining", answer: 0 },
            { question: "Close to activities", answer: 0 },
            { question: "View", answer: 0 },
            { question: "Close to gym", answer: 0 },
            { question: "Close to outside exit", answer: 0 },
            { question: "Direction (North, South, East, West)", answer: 0 },
        ],
    },
];

const getByAnswer = (sections, answer) => {
    let items = [];

    if (sections) {
        Object.values(sections).forEach((section) => {
            section.questions.forEach((question) => {
                if (question.answer === answer) {
                    items.push(question.question);
                }
            });
        });
    }

    return items;
};

const updateUser = async (user, sections, submittedLivingPreferences) => {
    if (user.uid) {
        await updateDocument("users", user.uid, {
            ...user,
            livingPreferences: sections,
            submittedLivingPreferences: submittedLivingPreferences
                ? submittedLivingPreferences
                : "",
        });
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case "LOADING":
            return { ...state, loading: true };

        case "INIT":
            if (action.user.livingPreferences) {
                return { ...state, loading: false, user: action.user };
            } else {
                return {
                    ...state,
                    loading: false,
                    user: { ...action.user, livingPreferences: defaultLivingPreferences },
                };
            }

        case "UPDATE": {
            const sections = JSON.parse(JSON.stringify(state.user.livingPreferences));
            const section = sections.find((s) => s.section === action.section);
            if (section) {
                const question = section.questions.find(
                    (q) => q.question === action.question
                );
                if (question) {
                    if (question.answer === action.answer) {
                        question.answer = 0;
                    } else {
                        question.answer = action.answer;
                    }
                }
            }

            updateUser(state.user, sections, state.user.submittedLivingPreferences);

            return { ...state, user: { ...state.user, livingPreferences: sections } };
        }

        case "SUBMIT":
            updateUser(
                state.user,
                state.user.livingPreferences,
                action.submit ? moment().format("MM/DD/YYYY") : ""
            );

            return {
                ...state,
                user: {
                    ...state.user,
                    submittedLivingPreferences: action.submit
                        ? moment().format("MM/DD/YYYY")
                        : "",
                },
            };

        case "RESET":
            return {
                ...state,
                user: {
                    ...state.user,
                    livingPreferences: defaultLivingPreferences,
                    submittedLivingPreferences: "",
                },
            };

        default:
            return state;
    }
};

const LivingPreferences = () => {
    const router = useRouter();
    const auth = useAuth();
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openSignIn, setOpenSignIn] = useState(false);
    const [state, dispatch] = useReducer(reducer, {
        loading: true,
        user: {
            submittedLivingPreferences: "",
            livingPreferences: defaultLivingPreferences,
        },
    });

    const userId = auth.user ? auth.user.uid : "";

    useEffect(() => {
        const init = async (uid) => {
            dispatch({ type: "LOADING" });

            const user = await readDocument("users", uid);
            if (user) {
                dispatch({ type: "INIT", user });
            }
        };

        if (userId) {
            init(userId);
        }
    }, [userId]);

    const handleChange = (section, question, answer) => {
        dispatch({
            type: "UPDATE",
            section,
            question,
            answer,
        });
    };

    const handleSubmit = () => {
        dispatch({ type: "SUBMIT", submit: true });
        window.scrollTo(0, 0);
    };

    const handleDownloadPdf = () => {
        router.push("/resources/living-preferences-pdf");
        window.scrollTo(0, 0);
    };

    const handleUpdate = () => {
        dispatch({ type: "SUBMIT", submit: false });
        window.scrollTo(0, 0);
    };

    const handleReset = () => {
        if (window.confirm("Clear all selections?")) {
            dispatch({ type: "RESET" });
            window.scrollTo(0, 0);
        }
    };

    console.log("state", state);

    return (
        <div className="bg-gray-50">
            <HomeHeader fixed />
            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-4 sm:px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        {
                            name: "Living Preferences",
                            href: "/resources/living-preferences",
                        },
                    ]}
                />
            </div>
            <div>
                <div className="hidden sm:block">
                    <div className="relative w-full h-40 sm:h-72 bg-black">
                        <Image
                            loader={imageLoader}
                            src="https://cdn.purpledoorfinders.com/purpledoorfinders/right-fit-tools"
                            alt="purple-door-finders-articles-for-memory-care"
                            layout="fill"
                            objectFit="cover"
                            objectPosition="0px -200px"
                            priority
                            unoptimized={false}
                            className="opacity-70"
                        />

                        <div className="flex justify-center">
                            <h1 className="absolute px-8 top-12 sm:top-32 text-3xl sm:text-5xl text-white font-headline font-bold">
                                Assisted Living Preferences
                            </h1>
                        </div>
                    </div>
                </div>

                <div className="sm:hidden">
                    <h1 className="px-4 text-3xl text-primary-500 font-headline font-bold">
                        Assisted Living Preferences
                    </h1>
                </div>

                {userId === "" ? (
                    <div className="container mx-auto mt-4 py-8 px-4 sm:px-16 bg-gray-300 rounded-lg">
                        <h4 className="font-bold text-2xl">
                            You need to be registed and signed in to use this tool and save
                            the information.
                        </h4>

                        <div className="mt-4 flex items-center">
                            <Button
                                className=""
                                onClick={() => {
                                    setOpenSignUp(true);

                                    gtag("event", "conversion", {
                                        send_to: "AW-10778046073/z0kvCMDQspMaEPncr5Mo",
                                    });

                                    oaiq("measure", "registration_completed", {
                                        type: "customer_action",
                                    });
                                }}
                            >
                                Create Account
                            </Button>
                        </div>

                        <div className="mt-4">
                            Already have an account?
                            <buton
                                className="ml-4 text-purple-500 hover:underline"
                                onClick={() => setOpenSignIn(true)}
                            >
                                Sign In
                            </buton>
                        </div>
                    </div>
                ) : null}

                {(userId ? !state.loading : true) &&
                !state.user.submittedLivingPreferences ? (
                    <div className="container mx-auto py-12 px-4 sm:px-16">
                        <div>
                            <h2 className="text-3xl text-primary-500 font-bold font-headline">
                                Get Started
                            </h2>

                            <p className="mt-8">
                                The Assisted Living Preferences tool allows you to determine
                                and evaluate your senior living communities, care options,
                                and specific unit features.
                            </p>

                            <p className="mt-4">
                                Upon completion of this tool, you can download your list of
                                preferences as a reference during your search, save to your
                                profile to access later, share and download.
                            </p>

                            <hr className="my-8 border-black" />

                            <div className="flex flex-col space-y-8">
                                {state.user.livingPreferences.map((section) => (
                                    <Section
                                        key={section.index}
                                        section={section}
                                        onChange={handleChange}
                                    />
                                ))}
                            </div>

                            <div className="mt-8 flex justify-end">
                                <Button
                                    className="uppercase"
                                    onClick={handleSubmit}
                                    disabled={!userId}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}

                {userId && !state.loading && state.user.submittedLivingPreferences ? (
                    <div className="container mx-auto py-12 px-4 sm:px-16">
                        <div>
                            <h2 className="text-3xl text-primary-500 font-bold font-headline">
                                You're All Done!
                            </h2>

                            <p className="mt-8">
                                Your personal Assisted Living Preferences is complete. You
                                now have a resource that prioritizes what is most important
                                to you for senior and assisted living.
                            </p>

                            <hr className="my-8 border-black" />

                            <p className="font-black">
                                The most important aspects of your Assisted Living
                                Preferences for living options
                            </p>

                            <ul className="mt-2 ml-5 flex flex-col space-y-1 list-disc">
                                {getByAnswer(state.user.livingPreferences, 3).map(
                                    (item) => (
                                        <li key={item}>{item}</li>
                                    )
                                )}
                            </ul>

                            <p className="mt-8 font-black">
                                Some other aspects of your Assisted Living Preferences for
                                living options that are somewhat important.
                            </p>

                            <ul className="mt-2 ml-5 flex flex-col space-y-1 list-disc">
                                {getByAnswer(state.user.livingPreferences, 2).map(
                                    (item) => (
                                        <li key={item}>{item}</li>
                                    )
                                )}
                            </ul>

                            <div className="mt-8 p-4 border-2 border-black">
                                <p>
                                    This living preferences tool is a planning resource to
                                    help you in your search for senior and assisted living.
                                    It does not guarantee your preferences.
                                </p>
                            </div>

                            <div className="mt-8 flex justify-start space-x-2 sm:space-x-8">
                                <Button className="uppercase" onClick={handleDownloadPdf}>
                                    Download as PDF
                                </Button>

                                <Button className="uppercase" onClick={handleUpdate}>
                                    Update Selections
                                </Button>

                                <Button
                                    variant="outlined"
                                    className="uppercase"
                                    onClick={handleReset}
                                >
                                    Clear All Selections
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            <SeoLinks />
            <HomeFooter />

            <SignUpSignIn
                openSignUp={openSignUp}
                openSignIn={openSignIn}
                setOpenSignUp={setOpenSignUp}
                setOpenSignIn={setOpenSignIn}
            />
        </div>
    );
};

const Section = ({ section, onChange }) => {
    return (
        <div className="">
            <h3 className="font-black">
                {section.index}. {section.section}
            </h3>

            <div className="mt-2 flex flex-col space-y-0">
                <div className="relative w-48 sm:w-96 h-8 mb-6 sm:mb-0">
                    <div className="absolute top-2 left-[140px] sm:left-[350px] text-xs text-center sm:whitespace-nowrap">
                        MOST IMPORTANT
                    </div>
                    <div className="absolute top-2 left-[300px] sm:left-[550px] text-xs text-center sm:whitespace-nowrap">
                        NOT IMPORTANT AT ALL
                    </div>
                </div>

                {section.questions.map((question, index) => (
                    <div
                        key={question.question}
                        className={`px-4 py-2 flex items-center ${
                            index % 2 === 1 ? "bg-gray-200" : "bg-gray-100"
                        }`}
                    >
                        <div className="w-48 sm:w-96">{question.question}</div>

                        <div className="flex space-x-16">
                            <input
                                type="checkbox"
                                name={question.question + ".3"}
                                value={question.answer === 3}
                                checked={question.answer === 3}
                                onChange={() =>
                                    onChange(section.section, question.question, 3)
                                }
                            />

                            <input
                                type="checkbox"
                                name={question.question + ".2"}
                                value={question.answer === 2}
                                checked={question.answer === 2}
                                onChange={() =>
                                    onChange(section.section, question.question, 2)
                                }
                            />

                            <input
                                type="checkbox"
                                name={question.question + ".1"}
                                value={question.answer === 1}
                                checked={question.answer === 1}
                                onChange={() =>
                                    onChange(section.section, question.question, 1)
                                }
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const getStaticProps = async () => {
    return {
        props: {
            meta: {
                title: "Assited Living Checklist",
                description:
                    "The Assisted Living Preferences tool allows you to determine and evaluate your senior living communities, care options, and specific unit features.",
            },
        },
    };
};

export default LivingPreferences;
